import { useQuery } from '@tanstack/react-query';
import type {
  FundingPledgeListItem,
  PledgeStatusEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

export interface FundingPledgesQueryParams {
  campaignId: string;
  page?: number | undefined;
  size?: number | undefined;
  sort?: string | undefined;
  status?: ReadonlyArray<PledgeStatusEnum> | undefined;
  userId?: string | undefined;
}

export interface FundingPledgesPage {
  items: ReadonlyArray<FundingPledgeListItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: string;
  status?: PledgeStatusEnum[];
  userId?: string;
}

const cleanQuery = (params: FundingPledgesQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  // BE 펀딩 API 는 페이지가 1부터다(PageRequest.of(page - 1, …)). 종전엔 0부터 보내 2페이지가 1페이지와 같았다(QA-398).
  if (params.page) out.page = Math.max(1, params.page);
  if (params.size) out.size = params.size;
  // sort disabled (F-08): FE enum vs BE entity column mismatch; BE @PageableDefault 사용
  if (params.status && params.status.length > 0) out.status = [...params.status];
  if (params.userId) out.userId = params.userId;
  return out;
};

export const fundingPledgesQueryKey = (params: FundingPledgesQueryParams) =>
  ['admin', 'funding-pledges', params] as const;

export const useFundingPledgesQuery = (params: FundingPledgesQueryParams) =>
  useQuery<FundingPledgesPage>({
    queryKey: fundingPledgesQueryKey(params),
    enabled: Boolean(params.campaignId),
    queryFn: async () => {
      const [data, meta] = await apiClient.request(
        'get',
        '/admin/v1/funding-campaigns/{campaignId}/pledges',
        {
          params: {
            path: { campaignId: params.campaignId },
            query: cleanQuery(params),
          },
        },
      );
      const payload = data as { items: FundingPledgeListItem[] };
      return { items: payload.items, meta };
    },
  });
